import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { ReactNode } from "react";
import type { WindowRecord } from "./WindowContext";
import { DEFAULT_MIN_WIDTH, DEFAULT_MIN_HEIGHT } from "./WindowContext";
import { useTheme } from "../../hooks/useTheme";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { testIds } from "../../lib/testIds";
import { getXpIcon, XP_ICON_FALLBACK } from "../../lib/desktop/iconMap";

interface WindowFrameProps {
  win: WindowRecord;
  title: string;
  children: ReactNode;
  onFocus: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
  onClose: () => void;
  onMove: (x: number, y: number) => void;
  onResize: (width: number, height: number) => void;
}

const TASKBAR_H = 58;
const TASKBAR_GAP = 8;

export function WindowFrame({
  win,
  title,
  children,
  onFocus,
  onMinimize,
  onMaximize,
  onClose,
  onMove,
  onResize,
}: WindowFrameProps) {
  const { tokens } = useTheme();
  const mobile = useMediaQuery("(max-width: 767px)");
  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, wx: 0, wy: 0 });
  const resizing = useRef(false);
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });

  function handleResizeMouseDown(e: React.MouseEvent) {
    if (win.maximized) return;
    if (mobile) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    onFocus();

    resizing.current = true;
    resizeStart.current = {
      mx: e.clientX,
      my: e.clientY,
      w: win.width,
      h: win.height,
    };
    const minW = win.options?.minWidth ?? DEFAULT_MIN_WIDTH;
    const minH = win.options?.minHeight ?? DEFAULT_MIN_HEIGHT;

    function onMouseMove(ev: MouseEvent) {
      if (!resizing.current) return;
      const dw = ev.clientX - resizeStart.current.mx;
      const dh = ev.clientY - resizeStart.current.my;
      const maxW = Math.max(minW, window.innerWidth - win.x - 8);
      const maxH = Math.max(minH, window.innerHeight - win.y - TASKBAR_H - TASKBAR_GAP - 8);
      const nw = Math.min(maxW, Math.max(minW, resizeStart.current.w + dw));
      const nh = Math.min(maxH, Math.max(minH, resizeStart.current.h + dh));
      onResize(nw, nh);
    }

    function onMouseUp() {
      resizing.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  function handleTitleMouseDown(e: React.MouseEvent) {
    if (win.maximized) return;
    if (mobile) return;
    if (e.button !== 0) return;
    // Don't start drag when clicking a titlebar control button.
    const target = e.target as HTMLElement;
    if (target.closest('.title-bar-controls')) return;
    e.preventDefault();
    onFocus();

    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, wx: win.x, wy: win.y };

    function onMouseMove(ev: MouseEvent) {
      if (!dragging.current) return;
      const nx = dragStart.current.wx + (ev.clientX - dragStart.current.mx);
      const ny = dragStart.current.wy + (ev.clientY - dragStart.current.my);
      const clampedX = Math.max(-600, Math.min(window.innerWidth - 100, nx));
      const clampedY = Math.max(0, Math.min(window.innerHeight - TASKBAR_H - TASKBAR_GAP - 34, ny));
      onMove(clampedX, clampedY);
    }

    function onMouseUp() {
      dragging.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  const winWidth = win.width || win.options?.width || 760;
  const winHeight = win.height || win.options?.height || 520;
  const minWidth = win.options?.minWidth ?? DEFAULT_MIN_WIDTH;
  const minHeight = win.options?.minHeight ?? DEFAULT_MIN_HEIGHT;
  const showResizeHandle = !win.maximized && !mobile;
  // NOTE: no `overflow: hidden` on the outer frame — that would clip
  // xp.css's 3px bottom navy edge AND our drop-shadow. The window-body
  // (inner) keeps its own overflow for content scrolling.
  const frameStyle: React.CSSProperties = win.maximized
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: TASKBAR_H + TASKBAR_GAP,
        zIndex: win.zIndex,
        display: "flex",
        flexDirection: "column",
      }
    : mobile
    ? {
        position: "fixed",
        top: 4,
        left: 4,
        right: 4,
        bottom: TASKBAR_H + TASKBAR_GAP,
        zIndex: win.zIndex,
        display: "flex",
        flexDirection: "column",
      }
    : {
        position: "fixed",
        top: Math.max(0, win.y),
        left: win.x,
        width: winWidth,
        height: winHeight,
        minWidth,
        minHeight,
        maxHeight: `calc(100vh - ${TASKBAR_H + TASKBAR_GAP + Math.max(0, win.y)}px)`,
        zIndex: win.zIndex,
        display: "flex",
        flexDirection: "column",
      };

  return (
    <AnimatePresence>
      {win.state === "open" && (
        <motion.div
          key={win.path}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.1 }}
          onMouseDown={onFocus}
          data-testid={testIds.window.frame(win.path)}
          // xp.css `.window` paints the XP raised + blue-edge chrome
          // with a 6-layer inset box-shadow (navy gradient frame) and
          // top-only border-radius (8px). Keep the upstream 3px bottom
          // padding so the bottom edge of the navy frame shows.
          className="window"
          style={frameStyle}
        >
          <div
            onMouseDown={handleTitleMouseDown}
            data-testid={testIds.window.titleBar(win.path)}
            className="title-bar"
            style={{
              cursor: win.maximized || mobile ? "default" : "move",
              flexShrink: 0,
              userSelect: "none",
            }}
          >
            {/* XP title-bar icon — real Windows XP shows the window's app
                icon at the far left of the title-bar before the title
                text. xp.css doesn't ship the slot; we inject an <img>
                that resolves via the desktop icon map (same source the
                taskbar + Start menu use). */}
            <img
              src={getXpIcon(win.path) ?? XP_ICON_FALLBACK}
              alt=""
              aria-hidden="true"
              draggable={false}
              style={{
                width: 16,
                height: 16,
                imageRendering: "pixelated",
                marginRight: 4,
                marginLeft: 1,
                flexShrink: 0,
              }}
            />
            <div className="title-bar-text" style={{ flex: 1 }}>{title}</div>
            <div className="title-bar-controls">
              <button
                aria-label="Minimize"
                title="Minimize"
                data-testid={testIds.window.minimize(win.path)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onMinimize();
                }}
              />
              <button
                aria-label={win.maximized ? "Restore" : "Maximize"}
                title={win.maximized ? "Restore" : "Maximize"}
                data-testid={testIds.window.maximize(win.path)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onMaximize();
                }}
              />
              <button
                aria-label="Close"
                title="Close"
                data-testid={testIds.window.close(win.path)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              />
            </div>
          </div>

          <div
            data-testid={testIds.window.content(win.path)}
            // `window-body` is xp.css's content slot; we extend with theme
            // tokens so child components keep reading the same CSS vars
            // they did in the Win95 build.
            className="window-body"
            style={{
              background: tokens.windowBg,
              backgroundImage: tokens.windowBgImage,
              color: tokens.text,
              padding: 12,
              overflowX: "hidden",
              overflowY: win.options?.contentOverflowY ?? "auto",
              flex: 1,
              fontFamily: "var(--xp-font-body)",
              fontSize: 12,
              lineHeight: 1.5,
              margin: 0,
              "--win-bg": tokens.windowBg,
              "--win-text": tokens.text,
              "--win-heading": tokens.heading,
              "--win-body": tokens.body,
              "--win-muted": tokens.muted,
              "--win-subtle": tokens.subtle,
              "--win-accent": tokens.accent,
              "--win-accent-border": tokens.accentBorder,
              "--win-border": tokens.border,
              "--win-card-bg": tokens.cardBg,
              "--win-placeholder-bg": tokens.placeholderBg,
              "--win-input-bg": tokens.inputBg,
              "--win-input-border": tokens.inputBorder,
              "--win-input-text": tokens.inputText,
              "--win-tag-bg": tokens.tagBg,
              "--win-tag-border": tokens.tagBorder,
              "--win-tag-text": tokens.tagText,
              "--win-code-bg": tokens.codeBg,
              "--win-success": tokens.success,
              "--win-success-muted": tokens.successMuted,
            } as React.CSSProperties}
          >
            {children}
          </div>
          {showResizeHandle && (
            <div
              onMouseDown={handleResizeMouseDown}
              data-testid={testIds.window.resizeHandle(win.path)}
              aria-label="Resize window"
              role="separator"
              // Real Windows XP doesn't paint a visible gripper triangle on
              // the corner — it just shows the resize cursor when the
              // mouse enters the 16×16 hit zone. Keep the invisible target
              // so resize still works.
              style={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 16,
                height: 16,
                cursor: "nwse-resize",
                background: "transparent",
                zIndex: 1,
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
